// src/pages/Dashboard/Dashboard.jsx
// -----------------------------------------------------------------------------
// Upgraded premium Support Overview Dashboard.
// Recreates the visual aesthetic of the uploaded screenshot:
//   - Minimalist header with breadcrumbs and black "+ Add New" button.
//   - Stat row with 4 cards: All tickets, Client replies, Staff replies, and Tickets without reply.
//   - Row 2: "Ticket reply time" (custom SVG line chart with tooltips) + "Ticket priority" (custom SVG donut chart).
//   - Row 3: "Average tickets created" (custom stacked SVG bar chart) + "Recent tickets" list with color status indicators.
// Fully connected to working backend APIs with 100% correct database data.
// -----------------------------------------------------------------------------
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import dashboardService from '../../api/dashboardService';
import ticketService, { mockStatuses, mockSeverities } from '../../api/ticketService';
import ticketStatusService from '../../api/ticketStatusService';
import ticketSeverityService from '../../api/ticketSeverityService';
import ticketTypeService from '../../api/ticketTypeService';
import userService from '../../api/userService';
import projectService from '../../api/projectService';
import Loader from '../../components/common/Loader';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { PlusIcon } from '../../components/common/Icons';
import './dashboard.css';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Core loading & state
  const [loading, setLoading] = useState(true);
  const [allTickets, setAllTickets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [users, setUsers] = useState([]);

  // Correct API counts
  const [stats, setStats] = useState({
    allTickets: 0,
    clientReplies: 0,
    staffReplies: 0,
    withoutReply: 0,
    resolvedRate: 0
  });

  const [exporting, setExporting] = useState(false);

  // Reply time & charts data
  const [avgReplyTimeText, setAvgReplyTimeText] = useState('0 mins');
  const [lineChartPoints, setLineChartPoints] = useState([]);
  const [donutSegments, setDonutSegments] = useState([]);
  const [totalActiveCount, setTotalActiveCount] = useState(0);
  const [recentTickets, setRecentTickets] = useState([]);

  // Distribution breakdowns: daily chart, status, type, and project
  const [distTab, setDistTab] = useState('daily'); // 'daily' | 'status' | 'type' | 'project'
  const [barChartData, setBarChartData] = useState([]);
  const [statusDist, setStatusDist] = useState([]);
  const [dateDist, setDateDist] = useState([]);
  const [typeDist, setTypeDist] = useState([]);
  const [projectDist, setProjectDist] = useState([]);

  // Date drilldown state
  const [selectedDateRecord, setSelectedDateRecord] = useState(null);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);

  // Interactive tooltip state for Line & Bar Charts
  const [tooltip, setTooltip] = useState({
    x: 0,
    y: 0,
    visible: false,
    header: '',
    content: ''
  });

  const containerRef = useRef(null);

  const handleOpenDateRecord = (day) => {
    if (!day) return;
    const targetDateObj = new Date(day.rawDate || day.isoDate || day.dateStr || day.label);
    const targetIso = !isNaN(targetDateObj.getTime())
      ? targetDateObj.toISOString().slice(0, 10)
      : day.isoDate || '';

    // Direct 1-click redirect to full Ticket Table showing all Open, Closed, Resolved tickets on that date
    navigate(`/tickets?date=${targetIso}`);
  };

  // Helper to dynamically locate resolution timestamp if resolvedDate is not directly set
  const getTicketResolvedDate = (t) => {
    if (t.resolvedDate) return new Date(t.resolvedDate);

    // Status ID 4 is "Resolved" in statuses table.
    const isResolved = Number(t.statusID) === 4 || String(t.statusName || '').toUpperCase() === 'RESOLVED' || String(t.statusName || '').toUpperCase() === 'CLOSED';
    if (isResolved) {
      // Fallback to last updated time, or create time plus a default handling offset if null
      const baseTime = t.lastUpdateDttm || t.createDate;
      if (baseTime) {
        return new Date(baseTime);
      }
      return new Date();
    }
    return null;
  };

  const getTicketDurationText = (ticket) => {
    if (!ticket.createDate) return '';
    const created = new Date(ticket.createDate);

    // Check if it's completed (Resolved or Closed)
    const isCompleted = ticket.statusKey === 'completed';
    if (isCompleted) {
      const resolved = getTicketResolvedDate(ticket);
      if (!resolved) return '';
      const diffMs = resolved - created;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      if (diffMins <= 0) return '⚡ Instant';
      if (diffMins < 60) return `⚡ ${diffMins}m`;
      const diffHrs = Math.floor(diffMins / 60);
      if (diffHrs < 24) return `⚡ ${diffHrs}h`;
      const diffDays = (diffHrs / 24).toFixed(1);
      return `⚡ ${diffDays}d`;
    } else {
      // It is open/pending/overdue
      const diffMs = Date.now() - created;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays <= 0) {
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        if (diffHrs <= 0) return '⏳ Just now';
        return `⏳ ${diffHrs}h`;
      }
      return `⏳ ${diffDays}d`;
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await dashboardService.exportReport('summary');
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Support_Overview_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel report exported successfully!');
    } catch (e) {
      console.error('Failed to export report:', e);
      toast.error('Failed to export Excel report.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const fetchAndCalculateDashboard = async () => {
      setLoading(true);
      try {
        const results = await Promise.allSettled([
          ticketService.getAll(),
          projectService.getAll(),
          userService.getAll(),
          ticketStatusService.getAll(),
          ticketSeverityService.getAll(),
          ticketTypeService.getAll(),
          dashboardService.getSummary(),
          dashboardService.getLongPendingTickets()
        ]);

        const ticketsData = results[0].status === 'fulfilled' ? results[0].value.data || [] : [];
        const projectsData = results[1].status === 'fulfilled' ? results[1].value.data || [] : [];
        const usersData = results[2].status === 'fulfilled' ? results[2].value.data?.data || results[2].value.data || [] : [];
        const statusesData = results[3].status === 'fulfilled' ? results[3].value.data?.data || results[3].value.data || [] : [];
        const severitiesData = results[4].status === 'fulfilled' ? results[4].value.data?.data || results[4].value.data || [] : [];
        const typesData = results[5].status === 'fulfilled' ? results[5].value.data?.data || results[5].value.data || [] : [];

        setAllTickets(ticketsData);
        setProjects(projectsData);
        setStatuses(statusesData);
        setSeverities(severitiesData);
        setTicketTypes(typesData);
        setUsers(usersData);

        // 1. Calculate Stat Numbers (All, Client Replies, Staff Replies, Without Reply)
        const calculatedAll = ticketsData.length;
        const calculatedClient = ticketsData.filter(t => t.clientOrgID && Number(t.statusID) !== 4).length;

        // Staff replies: In Progress (2), QA Testing (3), and Resolved (4) represent staff handling
        const calculatedStaff = ticketsData.filter(t => [2, 3, 4].includes(Number(t.statusID))).length;
        const calculatedWithout = ticketsData.filter(t => Number(t.statusID) === 1).length;
        const resolvedCount = ticketsData.filter(t => [3, 4].includes(Number(t.statusID)) || String(t.statusName || '').toUpperCase() === 'RESOLVED' || String(t.statusName || '').toUpperCase() === 'CLOSED').length;
        const calculatedRate = calculatedAll > 0 ? Math.round((resolvedCount / calculatedAll) * 100) : 0;

        setStats({
          allTickets: calculatedAll,
          clientReplies: calculatedClient,
          staffReplies: calculatedStaff,
          withoutReply: calculatedWithout,
          resolvedRate: calculatedRate
        });

        // 2. Ticket Reply Time (Average Resolution Time in minutes/hours/days)
        const resolvedList = ticketsData.filter(t => getTicketResolvedDate(t) && t.createDate);
        let calculatedAvgReply = '0 mins';
        if (resolvedList.length > 0) {
          let totalMs = 0;
          resolvedList.forEach(t => {
            const start = new Date(t.createDate);
            const end = getTicketResolvedDate(t);
            totalMs += Math.max(0, end - start);
          });
          const avgMins = Math.floor(totalMs / (1000 * 60) / resolvedList.length);
          if (avgMins >= 1440) {
            const days = (avgMins / 1440).toFixed(1);
            calculatedAvgReply = `${days} days`;
          } else if (avgMins >= 60) {
            const hrs = (avgMins / 60).toFixed(1);
            calculatedAvgReply = `${hrs} hrs`;
          } else {
            calculatedAvgReply = `${avgMins} mins`;
          }
        }
        setAvgReplyTimeText(calculatedAvgReply);

        // 3. Weekly Ticket Activity Trend (Created vs Solved per day)
        const last7Days = [];
        for (let i = 6; i >= 0; i--) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          last7Days.push(d);
        }

        const lineData = last7Days.map((day, idx) => {
          const dayStr = idx === 6 ? 'Today' : day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          const isoStr = day.toISOString().slice(0, 10);
          const fullDateStr = day.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

          const createdDayTickets = ticketsData.filter(t => {
            if (!t.createDate) return false;
            const cDate = new Date(t.createDate);
            return cDate.toDateString() === day.toDateString();
          });

          const solvedDayTickets = ticketsData.filter(t => {
            const rDate = getTicketResolvedDate(t);
            if (!rDate) return false;
            return rDate.toDateString() === day.toDateString();
          });

          return {
            rawDate: day,
            isoDate: isoStr,
            dateStr: fullDateStr,
            label: dayStr,
            created: createdDayTickets.length,
            solved: solvedDayTickets.length,
            tickets: createdDayTickets,
            solvedTickets: solvedDayTickets
          };
        });
        setLineChartPoints(lineData);
        setBarChartData(lineData);
        if (lineData.length > 0) {
          const todayRecord = lineData[lineData.length - 1];
          setSelectedDateRecord(todayRecord);
        }

        // 4. Ticket Priority Breakdown (Active tickets Critical, High, Medium, Low)
        const activeTickets = ticketsData.filter(t => Number(t.statusID) !== 4 && String(t.statusName || '').toUpperCase() !== 'RESOLVED' && String(t.statusName || '').toUpperCase() !== 'CLOSED');
        const totalActive = activeTickets.length;
        setTotalActiveCount(totalActive);

        const priorityCategories = [
          {
            name: 'Critical',
            cls: 'crit',
            color: '#a855f7',
            value: activeTickets.filter(t => {
              const matched = severitiesData.find(s => Number(s.severityID || s.severityId) === Number(t.severityID));
              return (matched?.severityName?.toLowerCase() === 'critical') || Number(t.severityID) === 4;
            }).length
          },
          {
            name: 'High',
            cls: 'high',
            color: '#3b82f6',
            value: activeTickets.filter(t => {
              const matched = severitiesData.find(s => Number(s.severityID || s.severityId) === Number(t.severityID));
              return (matched?.severityName?.toLowerCase() === 'high') || Number(t.severityID) === 3;
            }).length
          },
          {
            name: 'Medium',
            cls: 'med',
            color: '#f97316',
            value: activeTickets.filter(t => {
              const matched = severitiesData.find(s => Number(s.severityID || s.severityId) === Number(t.severityID));
              return (matched?.severityName?.toLowerCase() === 'medium') || Number(t.severityID) === 2 || !t.severityID;
            }).length
          },
          {
            name: 'Low',
            cls: 'low',
            color: '#10b981',
            value: activeTickets.filter(t => {
              const matched = severitiesData.find(s => Number(s.severityID || s.severityId) === Number(t.severityID));
              return (matched?.severityName?.toLowerCase() === 'low') || Number(t.severityID) === 1;
            }).length
          }
        ];

        let accumulatedPct = 0;
        const mappedSegments = priorityCategories.map(cat => {
          const pct = totalActive > 0 ? (cat.value / totalActive) * 100 : 0;
          const segment = {
            ...cat,
            percentage: pct,
            startOffset: accumulatedPct
          };
          accumulatedPct += pct;
          return segment;
        });
        setDonutSegments(mappedSegments);

        // 5. Workflow & Ticket Distribution Breakdowns (Status, Date, Type, Project)
        const effectiveStatuses = statusesData.length > 0 ? statusesData : mockStatuses;
        const calculatedStatusDist = effectiveStatuses.map(st => {
          const count = ticketsData.filter(t => Number(t.statusID) === Number(st.statusID)).length;
          const pct = calculatedAll > 0 ? Math.round((count / calculatedAll) * 100) : 0;
          const stName = st.statusName || `Status ${st.statusID}`;
          const normKey = String(stName).toLowerCase().replace(/[\s\-_]+/g, '');
          let color = '#ea580c';
          if (normKey.includes('progress') || normKey.includes('dev')) color = '#2563eb';
          else if (normKey.includes('qa') || normKey.includes('test') || normKey.includes('review')) color = '#9333ea';
          else if (normKey.includes('resolv') || normKey.includes('fix')) color = '#059669';
          else if (normKey.includes('close') || normKey.includes('done') || normKey.includes('complete')) color = '#0f766e';
          else if (normKey.includes('reopen')) color = '#d97706';

          return {
            id: st.statusID,
            name: stName,
            count,
            percentage: pct,
            color
          };
        });
        setStatusDist(calculatedStatusDist);

        // Date breakdown list
        const calculatedDateDist = lineData.map((d, i) => ({
          id: d.isoDate || i,
          isoDate: d.isoDate,
          name: `${d.label} — ${d.dateStr}`,
          shortLabel: d.label,
          count: d.created,
          solved: d.solved,
          percentage: calculatedAll > 0 ? Math.round((d.created / calculatedAll) * 100) : 0,
          color: d.created > 0 ? '#ea580c' : '#94a3b8',
          rawDay: d
        }));
        setDateDist(calculatedDateDist);

        const typeColorPalette = ['#f97316', '#3b82f6', '#10b981', '#a855f7', '#06b6d4', '#ec4899'];
        const calculatedTypeDist = (typesData.length > 0 ? typesData : [
          { ticketTypeID: 1, typeName: 'Bug' },
          { ticketTypeID: 2, typeName: 'Task' },
          { ticketTypeID: 3, typeName: 'Story' }
        ]).map((tp, i) => {
          const typeID = tp.ticketTypeID || tp.ticketTypeId;
          const count = ticketsData.filter(t => Number(t.ticketTypeID || t.ticketTypeId) === Number(typeID)).length;
          const pct = calculatedAll > 0 ? Math.round((count / calculatedAll) * 100) : 0;
          return {
            id: typeID,
            name: tp.typeName,
            count,
            percentage: pct,
            color: typeColorPalette[i % typeColorPalette.length]
          };
        });
        setTypeDist(calculatedTypeDist);

        const projColorPalette = ['#ea580c', '#2563eb', '#059669', '#9333ea', '#0f766e', '#d97706'];
        const calculatedProjDist = projectsData.map((pj, i) => {
          const projID = pj.projectID || pj.projectId;
          const count = ticketsData.filter(t => Number(t.projectID || t.projectId) === Number(projID)).length;
          const pct = calculatedAll > 0 ? Math.round((count / calculatedAll) * 100) : 0;
          return {
            id: projID,
            name: pj.projectName,
            count,
            percentage: pct,
            color: projColorPalette[i % projColorPalette.length]
          };
        });
        setProjectDist(calculatedProjDist);

        // 6. Recent Tickets List (Sorted by date descending)
        const sortedTickets = [...ticketsData].sort((a, b) => new Date(b.createDate) - new Date(a.createDate));

        const recentList = sortedTickets.slice(0, 6).map(t => {
          const cDate = new Date(t.createDate);
          const dateFormatted = cDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });

          // Map severity matching the Ticket Priority colors
          const matchedSev = severitiesData.find(s => Number(s.severityID || s.severityId) === Number(t.severityID))
            || mockSeverities.find(s => Number(s.severityID) === Number(t.severityID));
          const sevName = matchedSev?.severityName || t.severityName || 'Medium';
          const sevKey = String(sevName).toLowerCase();

          let mappedStatus = 'open';
          const statText = String(t.statusName || '').toUpperCase();
          if (statText === 'CLOSED' || statText === 'RESOLVED' || statText === 'COMPLETED') {
            mappedStatus = 'completed';
          } else if (statText === 'IN PROGRESS' || statText === 'PENDING') {
            mappedStatus = 'pending';
          } else if (statText === 'OVERDUE') {
            mappedStatus = 'overdue';
          } else {
            // Overdue if open and raised > 3 days ago
            const daysOpen = (Date.now() - cDate.getTime()) / (1000 * 60 * 60 * 24);
            if (daysOpen > 3 && Number(t.statusID) === 1) {
              mappedStatus = 'overdue';
            } else if (Number(t.statusID) === 2) {
              mappedStatus = 'pending';
            } else if (Number(t.statusID) === 3 || Number(t.statusID) === 4) {
              mappedStatus = 'completed';
            }
          }

          return {
            ...t,
            dateStr: dateFormatted,
            statusKey: mappedStatus,
            severityName: sevName,
            severityKey: sevKey
          };
        });

        setRecentTickets(recentList);

      } catch (err) {
        console.error('Failed to load support dashboard', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAndCalculateDashboard();
  }, []);

  if (loading) return <Loader label="Loading support overview..." />;

  // Coordinate math for Line Chart SVG
  const chartWidth = 600;
  const chartHeight = 180;
  const paddingX = 40;
  const paddingY = 24;

  const allActivityValues = lineChartPoints.flatMap(p => [p.created || 0, p.solved || 0]);
  const maxLineVal = Math.max(...allActivityValues, 4);

  const svgPointsCreated = lineChartPoints.map((point, index) => {
    const x = paddingX + (index * (chartWidth - paddingX * 2)) / (lineChartPoints.length - 1 || 1);
    const y = chartHeight - paddingY - ((point.created || 0) / maxLineVal) * (chartHeight - paddingY * 2);
    return { x, y, point, label: point.label, dateStr: point.dateStr };
  });

  const svgPointsSolved = lineChartPoints.map((point, index) => {
    const x = paddingX + (index * (chartWidth - paddingX * 2)) / (lineChartPoints.length - 1 || 1);
    const y = chartHeight - paddingY - ((point.solved || 0) / maxLineVal) * (chartHeight - paddingY * 2);
    return { x, y, point, label: point.label, dateStr: point.dateStr };
  });

  // SVG Cubic Bezier Curve Generation for smooth wave lines
  const getCurvePath = (pts) => {
    if (pts.length === 0) return '';
    let path = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const curr = pts[i];
      const next = pts[i + 1];
      const cp1x = curr.x + (next.x - curr.x) / 2;
      const cp1y = curr.y;
      const cp2x = curr.x + (next.x - curr.x) / 2;
      const cp2y = next.y;
      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y}`;
    }
    return path;
  };

  const pathCreatedD = getCurvePath(svgPointsCreated);
  const pathSolvedD = getCurvePath(svgPointsSolved);

  const areaCreatedD = svgPointsCreated.length > 0
    ? `${pathCreatedD} L ${svgPointsCreated[svgPointsCreated.length - 1].x} ${chartHeight - paddingY} L ${svgPointsCreated[0].x} ${chartHeight - paddingY} Z`
    : '';

  // Tooltip handler for line chart
  const handleMouseMoveDot = (e, pt) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setTooltip({
      x,
      y,
      visible: true,
      header: pt.dateStr,
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#f97316', display: 'inline-block' }} />
            <span>{pt.point.created} Created</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', display: 'inline-block' }} />
            <span>{pt.point.solved} Solved</span>
          </div>
        </div>
      )
    });
  };

  const handleMouseLeaveDot = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  // Tooltip handler for stacked bar chart columns
  const handleMouseMoveBar = (e, bar) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    let dateHeader = bar.label;
    if (bar.label === 'Today') {
      dateHeader = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } else {
      dateHeader = `${bar.label}, 2026`;
    }

    setTooltip({
      x,
      y,
      visible: true,
      header: dateHeader,
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#f97316', display: 'inline-block' }} />
            <span>{bar.solved} Solved</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#fdba74', display: 'inline-block' }} />
            <span>{bar.created} Created</span>
          </div>
        </div>
      )
    });
  };

  // Stacked Bar Chart Y-axis tick calculations
  const barSums = barChartData.map(b => b.solved + b.created);
  const rawMax = Math.max(...barSums, 0);

  // Custom smart scaling: if rawMax is 1 or less, tick steps should be 0.5 (axis max is 3)
  let barMax = 10;
  let yAxisTicks = [];

  if (rawMax <= 1) {
    barMax = 3;
    yAxisTicks = [3, 2, 1, 0];
  } else {
    const step = Math.ceil(rawMax / 6);
    barMax = step * 6;
    for (let i = 6; i >= 0; i--) {
      yAxisTicks.push(step * i);
    }
  }

  // Pure SVG Arc generator for circular gauge segments
  const getArcPath = (startPct, endPct, r = 45, cx = 70, cy = 75) => {
    const startAngle = 180 - (startPct * 1.8);
    const endAngle = 180 - (endPct * 1.8);

    const x1 = cx + r * Math.cos((startAngle * Math.PI) / 180);
    const y1 = cy - r * Math.sin((startAngle * Math.PI) / 180);
    const x2 = cx + r * Math.cos((endAngle * Math.PI) / 180);
    const y2 = cy - r * Math.sin((endAngle * Math.PI) / 180);

    const largeArcFlag = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;

    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
  };

  return (
    <div className="dash" ref={containerRef}>
      {/* ---------- Row 1: Five Stat Cards with direct redirects ---------- */}
      <div className="dash-stats-grid">
        {/* Card 1: All Tickets */}
        <div className="dash-stat-card" onClick={() => navigate('/tickets')} style={{ cursor: 'pointer' }}>
          <div className="dash-stat-info">
            <span className="dash-stat-label">All tickets</span>
            <span className="dash-stat-value">{stats.allTickets.toLocaleString()}</span>
          </div>
          <div className="dash-stat-icon-wrapper" style={{ background: '#fff7ed', color: '#ea580c', borderColor: '#ffedd5' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
        </div>

        {/* Card 2: Client Replies */}
        <div className="dash-stat-card" onClick={() => navigate('/tickets?filter=client_replies')} style={{ cursor: 'pointer' }}>
          <div className="dash-stat-info">
            <span className="dash-stat-label">Client replies</span>
            <span className="dash-stat-value">{stats.clientReplies.toLocaleString()}</span>
          </div>
          <div className="dash-stat-icon-wrapper" style={{ background: '#eef2ff', color: '#4f46e5', borderColor: '#e0e7ff' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 9a3 3 0 0 1 0 6v3a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3a3 3 0 0 1 0-6V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
              <line x1="9" y1="4" x2="9" y2="20" strokeDasharray="3 3" />
              <line x1="15" y1="4" x2="15" y2="20" strokeDasharray="3 3" />
            </svg>
          </div>
        </div>

        {/* Card 3: Staff Replies */}
        <div className="dash-stat-card" onClick={() => navigate('/tickets?filter=staff_replies')} style={{ cursor: 'pointer' }}>
          <div className="dash-stat-info">
            <span className="dash-stat-label">Staff replies</span>
            <span className="dash-stat-value">{stats.staffReplies.toLocaleString()}</span>
          </div>
          <div className="dash-stat-icon-wrapper" style={{ background: '#f0fdf4', color: '#16a34a', borderColor: '#dcfce7' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="14" rx="2" />
              <line x1="12" y1="4" x2="12" y2="18" />
              <line x1="3" y1="9" x2="12" y2="9" />
              <line x1="12" y1="13" x2="21" y2="13" />
            </svg>
          </div>
        </div>

        {/* Card 4: Tickets Without Reply */}
        <div className="dash-stat-card" onClick={() => navigate('/tickets?filter=without_reply')} style={{ cursor: 'pointer' }}>
          <div className="dash-stat-info">
            <span className="dash-stat-label">Tickets without reply</span>
            <span className="dash-stat-value">{stats.withoutReply.toLocaleString()}</span>
          </div>
          <div className="dash-stat-icon-wrapper" style={{ background: '#fff1f2', color: '#e11d48', borderColor: '#ffe4e6' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="12" r="6" />
              <circle cx="12" cy="12" r="2" fill="currentColor" />
              <line x1="12" y1="2" x2="12" y2="4" />
              <line x1="12" y1="20" x2="12" y2="22" />
              <line x1="2" y1="12" x2="4" y2="12" />
              <line x1="20" y1="12" x2="22" y2="12" />
            </svg>
          </div>
        </div>

        {/* Card 5: Resolution Rate */}
        <div className="dash-stat-card" onClick={() => navigate('/tickets?filter=resolved')} style={{ borderLeft: '4px solid #f97316', cursor: 'pointer' }}>
          <div className="dash-stat-info">
            <span className="dash-stat-label">Resolution Rate</span>
            <span className="dash-stat-value" style={{ color: '#ea580c' }}>{stats.resolvedRate}%</span>
          </div>
          <div className="dash-stat-icon-wrapper" style={{ background: '#fff7ed', color: '#ea580c', borderColor: '#ffedd5' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
        </div>
      </div>

      {/* ---------- Row 2: Weekly Ticket Activity & Ticket Priority ---------- */}
      <div className="dash-row-2col">
        {/* Weekly Ticket Activity splined dual-line chart */}
        <div className="dash-card">
          <div className="dash-card-header" style={{ flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <h2 style={{ margin: 0 }}>Weekly Ticket Activity</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#ea580c' }}>
                  {stats.allTickets} Total
                </span>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>•</span>
                <span style={{ fontSize: '11px', fontWeight: '600', color: '#059669', background: '#ecfdf5', padding: '2px 7px', borderRadius: '4px' }}>
                  ⚡ {avgReplyTimeText} avg. turnaround
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#475569', fontWeight: '600' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#f97316', display: 'inline-block' }} />
                <span>Created</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#475569', fontWeight: '600' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', display: 'inline-block' }} />
                <span>Solved</span>
              </div>
            </div>
          </div>

          <div className="dash-line-chart-wrapper">
            <svg className="dash-chart-svg" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none">
              <defs>
                <linearGradient id="chart-created-gradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f97316" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="#f97316" stopOpacity="0.0" />
                </linearGradient>
              </defs>

              {/* Grid Lines */}
              <line x1={paddingX} y1={paddingY} x2={chartWidth - paddingX} y2={paddingY} className="dash-chart-grid-line" />
              <line x1={paddingX} y1={chartHeight / 2} x2={chartWidth - paddingX} y2={chartHeight / 2} className="dash-chart-grid-line" />
              <line x1={paddingX} y1={chartHeight - paddingY} x2={chartWidth - paddingX} y2={chartHeight - paddingY} className="dash-chart-grid-line" />

              {/* Gradient Area for Created curve */}
              {areaCreatedD && <path d={areaCreatedD} fill="url(#chart-created-gradient)" />}

              {/* Created Line (Sunset Orange) */}
              {pathCreatedD && (
                <path
                  d={pathCreatedD}
                  fill="none"
                  stroke="#f97316"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {/* Solved Line (Emerald Green) */}
              {pathSolvedD && (
                <path
                  d={pathSolvedD}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2.5"
                  strokeDasharray="5 4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {/* Vertex Dots - Created */}
              {svgPointsCreated.map((pt, idx) => (
                <circle
                  key={`created-${idx}`}
                  cx={pt.x}
                  cy={pt.y}
                  r={6}
                  fill="#ffffff"
                  stroke="#f97316"
                  strokeWidth="3"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleOpenDateRecord(pt)}
                  onMouseMove={(e) => handleMouseMoveDot(e, pt)}
                  onMouseLeave={handleMouseLeaveDot}
                />
              ))}

              {/* Vertex Dots - Solved */}
              {svgPointsSolved.map((pt, idx) => (
                <circle
                  key={`solved-${idx}`}
                  cx={pt.x}
                  cy={pt.y}
                  r={5}
                  fill="#ffffff"
                  stroke="#10b981"
                  strokeWidth="2.5"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleOpenDateRecord(pt)}
                  onMouseMove={(e) => handleMouseMoveDot(e, pt)}
                  onMouseLeave={handleMouseLeaveDot}
                />
              ))}

              {/* X Axis labels */}
              {svgPointsCreated.map((pt, idx) => (
                <text
                  key={idx}
                  x={pt.x}
                  y={chartHeight - 4}
                  textAnchor="middle"
                  fill="#64748b"
                  fontSize="11"
                  fontWeight="700"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleOpenDateRecord(pt)}
                >
                  {pt.label}
                </text>
              ))}
            </svg>

            {/* Custom Interactive Tooltip */}
            {tooltip.visible && (
              <div
                className="dash-chart-tooltip"
                style={{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }}
              >
                <div className="dash-chart-tooltip-header">{tooltip.header}</div>
                <div>{tooltip.content}</div>
              </div>
            )}
          </div>
        </div>

        {/* Ticket Priority donut breakdown chart */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h2>Ticket priority</h2>
          </div>

          <div className="dash-donut-container">
            <svg className="dash-donut-svg" viewBox="0 0 140 85">
              {/* Background empty track */}
              <path
                d={getArcPath(0, 100)}
                fill="transparent"
                stroke="#f1f5f9"
                strokeWidth="14"
                strokeLinecap="round"
              />
              {/* Active segments drawn clockwise (Purple, Blue, Orange, Green matching mockup exactly) */}
              {donutSegments.map((seg, idx) => {
                if (seg.percentage === 0) return null;
                const startPct = seg.startOffset;
                const endPct = seg.startOffset + seg.percentage;

                return (
                  <path
                    key={idx}
                    d={getArcPath(startPct, endPct)}
                    fill="transparent"
                    stroke={seg.color}
                    strokeWidth="14"
                    strokeLinecap="round"
                  />
                );
              })}
            </svg>

            {/* Center Summary Widget */}
            <div className="dash-donut-center" style={{ bottom: '10px' }}>
              <span className="dash-donut-center-lbl">Total active tickets</span>
              <span className="dash-donut-center-val">{totalActiveCount.toLocaleString()}</span>
            </div>
          </div>

          {/* Donut Legend with colored bullet dots */}
          <div className="dash-donut-legend">
            {donutSegments.map((seg, idx) => (
              <div
                key={idx}
                className="dash-donut-legend-item"
                onClick={() => {
                  const sevObj = severities.find(s => s.severityName?.toLowerCase() === seg.name.toLowerCase());
                  if (sevObj) {
                    navigate(`/tickets?severityID=${sevObj.severityID || sevObj.severityId}`);
                  } else {
                    navigate('/tickets');
                  }
                }}
                style={{ cursor: 'pointer' }}
                title={`Click to view ${seg.name} priority tickets`}
              >
                <span
                  className="dash-donut-legend-dot"
                  style={{
                    backgroundColor: seg.color,
                    width: '9px',
                    height: '9px',
                    borderRadius: '50%',
                    display: 'inline-block',
                    flexShrink: 0
                  }}
                />
                <span style={{ fontWeight: '600', color: '#475569', fontSize: '12px' }}>
                  {seg.name} ({seg.value})
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---------- Row 3: Daily Ticket Activity & Recent Tickets ---------- */}
      <div className="dash-row-3col">
        {/* Daily Ticket Activity & Distribution Card */}
        <div className="dash-card">
          <div className="dash-card-header" style={{ flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <h2 style={{ margin: 0 }}>
                {distTab === 'daily' ? 'Average tickets created' : 'Ticket Distribution'}
              </h2>
              <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: '600' }}>
                {distTab === 'daily'
                  ? 'Click on any date to inspect all tickets logged'
                  : `${stats.allTickets} Total tickets across workflow stages`}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              {distTab === 'daily' && (
                <div className="dash-bar-legend">
                  <div className="dash-bar-legend-item">
                    <span className="dash-bar-legend-color" style={{ backgroundColor: '#f97316' }} />
                    <span>Avg. Ticket Solved</span>
                  </div>
                  <div className="dash-bar-legend-item">
                    <span className="dash-bar-legend-color" style={{ backgroundColor: '#fdba74' }} />
                    <span>Avg. Ticket Created</span>
                  </div>
                </div>
              )}

              <div className="dash-dist-tabs">
                <button
                  type="button"
                  className={`dash-dist-tab-btn ${distTab === 'daily' ? 'active' : ''}`}
                  onClick={() => setDistTab('daily')}
                >
                  Daily Chart
                </button>
                <button
                  type="button"
                  className={`dash-dist-tab-btn ${distTab === 'status' ? 'active' : ''}`}
                  onClick={() => setDistTab('status')}
                >
                  By Status
                </button>
                <button
                  type="button"
                  className={`dash-dist-tab-btn ${distTab === 'type' ? 'active' : ''}`}
                  onClick={() => setDistTab('type')}
                >
                  By Type
                </button>
                <button
                  type="button"
                  className={`dash-dist-tab-btn ${distTab === 'project' ? 'active' : ''}`}
                  onClick={() => setDistTab('project')}
                >
                  By Project
                </button>
              </div>
            </div>
          </div>

          {distTab === 'daily' ? (
            <div>
              {/* Stacked Bar Chart */}
              <div className="dash-bar-chart-wrapper">
                <div className="dash-bar-chart-y-axis">
                  {yAxisTicks.map((tick, i) => (
                    <span key={i}>{tick.toLocaleString()}</span>
                  ))}
                </div>

                <div className="dash-bar-chart-main">
                  <div className="dash-bar-grid-lines">
                    {yAxisTicks.map((_, i) => (
                      <div key={i} className="dash-bar-grid-line" />
                    ))}
                  </div>

                  <div className="dash-bar-chart-container">
                    {barChartData.map((bar, idx) => {
                      const solvedHeight = barMax > 0 ? (bar.solved / barMax) * 100 : 0;
                      const createdHeight = barMax > 0 ? (bar.created / barMax) * 100 : 0;
                      const totalDay = (bar.created || 0) + (bar.solved || 0);

                      return (
                        <div
                          key={idx}
                          className="dash-bar-col"
                          onClick={() => handleOpenDateRecord(bar)}
                          onMouseMove={(e) => handleMouseMoveBar(e, bar)}
                          onMouseLeave={handleMouseLeaveDot}
                          title={`Click to open all tickets for ${bar.dateStr || bar.label}`}
                        >
                          <div className="dash-bar-val-badge">
                            {totalDay > 0 ? totalDay : ''}
                          </div>
                          <div className="dash-bar-stacked-track">
                            <div
                              className="dash-bar-segment created"
                              style={{ height: `${createdHeight}%` }}
                            />
                            <div
                              className="dash-bar-segment solved"
                              style={{ height: `${solvedHeight}%` }}
                            />
                          </div>
                          <span className="dash-bar-label">{bar.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Interactive 7-Day Date Pill Bar */}
              <div className="dash-date-bar-container">
                {barChartData.map((day, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="dash-date-card-btn"
                    onClick={() => handleOpenDateRecord(day)}
                    title={`Click to open all tickets for ${day.dateStr || day.label}`}
                  >
                    <span className="dash-date-card-lbl">{day.label}</span>
                    <div className="dash-date-card-stats">
                      <span className="dash-date-card-badge-c" title={`${day.created || 0} created`}>
                        +{day.created || 0}
                      </span>
                      <span className="dash-date-card-badge-s" title={`${day.solved || 0} solved`}>
                        ✓{day.solved || 0}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Clean Footer Bar */}
              <div className="dash-bar-footer-stats">
                <span>
                  Click any date card to view all tickets created and solved.
                </span>
                <button
                  type="button"
                  className="dash-card-action-btn"
                  onClick={() => navigate('/tickets')}
                >
                  View all tickets in table →
                </button>
              </div>
            </div>
          ) : (
            <div className="dash-dist-list">
              {(distTab === 'status' ? statusDist : distTab === 'type' ? typeDist : projectDist).length === 0 ? (
                <div className="dash-empty" style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>
                  No records found.
                </div>
              ) : (
                (distTab === 'status' ? statusDist : distTab === 'type' ? typeDist : projectDist).map(item => (
                  <div
                    key={item.id}
                    className="dash-dist-row"
                    onClick={() => {
                      if (distTab === 'status') {
                        navigate(`/tickets?status=${item.id}`);
                      } else if (distTab === 'project') {
                        navigate(`/tickets?project=${item.id}`);
                      } else {
                        navigate('/tickets');
                      }
                    }}
                    title="Click to view corresponding tickets in table"
                  >
                    <div className="dash-dist-row-header">
                      <div className="dash-dist-row-title">
                        {distTab === 'status' ? (
                          <StatusBadge value={item.name} />
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: item.color }} />
                            {item.name}
                          </span>
                        )}
                      </div>
                      <div className="dash-dist-row-stats">
                        <span className="dash-dist-count">{item.count} {item.count === 1 ? 'ticket' : 'tickets'}</span>
                        <span className="dash-dist-pct">{item.percentage}%</span>
                      </div>
                    </div>

                    <div className="dash-dist-track">
                      <div
                        className="dash-dist-bar"
                        style={{
                          width: `${item.percentage}%`,
                          backgroundColor: item.color
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Recent Tickets list */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h2>Recent tickets</h2>
            <button className="dash-card-action-btn" onClick={() => navigate('/tickets')}>
              View all
            </button>
          </div>

          <div className="dash-tickets-list">
            {recentTickets.length === 0 ? (
              <div className="dash-empty" style={{ padding: '80px 0' }}>No tickets found in the database.</div>
            ) : (
              recentTickets.map(ticket => (
                <div
                  key={ticket.ticketID}
                  className={`dash-ticket-item severity-${ticket.severityKey}`}
                  onClick={() => navigate(`/tickets/${ticket.ticketID}`)}
                >
                  <div className="dash-ticket-left">
                    <span className="dash-ticket-date">{ticket.dateStr}</span>
                    <div className="dash-ticket-details">
                      <span className="dash-ticket-title" title={ticket.title}>{ticket.title}</span>
                      <span className="dash-ticket-desc" title={ticket.description}>{ticket.description}</span>
                    </div>
                  </div>
                  <div className="dash-ticket-right">
                    <span className={`dash-badge-severity ${ticket.severityKey}`}>
                      {ticket.severityName}
                    </span>
                    <span className={`dash-badge-status ${ticket.statusKey}`}>
                      {ticket.statusKey}
                    </span>
                    <span className="dash-ticket-action">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="7" y1="17" x2="17" y2="7" />
                        <polyline points="7 7 17 7 17 17" />
                      </svg>
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ---------- Date Activity Drilldown Modal ---------- */}
      {isDateModalOpen && selectedDateRecord && (
        <div className="modal-overlay" onClick={() => setIsDateModalOpen(false)}>
          <div
            className="modal-box modal-lg"
            style={{ maxWidth: '820px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  📅 Ticket Activity for {selectedDateRecord.dateStr}
                </h3>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <span className="badge badge-status-open">
                    +{selectedDateRecord.created || 0} Created
                  </span>
                  <span className="badge badge-status-resolved">
                    ✓{selectedDateRecord.solved || 0} Solved
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsDateModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body" style={{ padding: '20px 24px' }}>
              {(!selectedDateRecord.tickets || selectedDateRecord.tickets.length === 0) ? (
                <div className="dash-empty" style={{ padding: '40px 0' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>📭</div>
                  <p style={{ margin: 0, fontSize: '14.5px', color: '#64748b', fontWeight: '600' }}>
                    No tickets recorded on <strong>{selectedDateRecord.dateStr}</strong>.
                  </p>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ marginTop: '16px' }}
                    onClick={() => {
                      setIsDateModalOpen(false);
                      navigate('/tickets');
                    }}
                  >
                    View All Tickets
                  </button>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="dash-modal-ticket-table">
                    <thead>
                      <tr>
                        <th>Ticket #</th>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Severity</th>
                        <th>Created Time</th>
                        <th style={{ textAlign: 'right' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedDateRecord.tickets.map((t) => {
                        const sev = severities.find(s => Number(s.severityID || s.severityId) === Number(t.severityID))
                          || mockSeverities.find(s => Number(s.severityID) === Number(t.severityID));
                        const stat = statuses.find(s => Number(s.statusID) === Number(t.statusID))
                          || mockStatuses.find(s => Number(s.statusID) === Number(t.statusID));
                        const timeFormatted = t.createDate
                          ? new Date(t.createDate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                          : '-';

                        return (
                          <tr key={t.ticketID}>
                            <td>
                              <strong>{t.ticketNumber}</strong>
                            </td>
                            <td>
                              <div style={{ fontWeight: '600', maxWidth: '240px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {t.title}
                              </div>
                            </td>
                            <td>
                              <StatusBadge value={stat?.statusName || t.statusName || 'Open'} />
                            </td>
                            <td>
                              <StatusBadge value={sev?.severityName || t.severityName || 'Medium'} />
                            </td>
                            <td style={{ color: '#64748b', fontSize: '12px' }}>
                              {timeFormatted}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                style={{ padding: '4px 10px', fontSize: '12px' }}
                                onClick={() => {
                                  setIsDateModalOpen(false);
                                  navigate(`/tickets/${t.ticketID}`);
                                }}
                              >
                                View Ticket
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 24px', borderTop: '1px solid #e2e8f0' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setIsDateModalOpen(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setIsDateModalOpen(false);
                  navigate(`/tickets?date=${selectedDateRecord.isoDate}`);
                }}
              >
                Open in Full Ticket Table →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}